import { useEffect, useState, type KeyboardEvent } from 'react';
import { Loader2 } from 'lucide-react';
import type { CrmDealListItem } from '@ip/shared-types';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { t } from '@/i18n';
import { formatMoney } from '@/utils/currency';

const LOST_REASONS = ['price', 'competition', 'noBudget', 'noDecision', 'noContact', 'other'] as const;
const MIN_REASON_LENGTH = 3;

type CloseMode = 'won' | 'lost';

/** Everything that differs between the two confirmations, read once per render. */
const MODES: Record<
  CloseMode,
  {
    titleKey: string;
    labelKey: string;
    confirmKey: string;
    variant: 'default' | 'destructive';
    /** Lost needs a reason of at least MIN_REASON_LENGTH; won takes an optional note. */
    requiresText: boolean;
  }
> = {
  won: {
    titleKey: 'crm.deals.close.wonTitle',
    labelKey: 'crm.deals.close.wonNote',
    confirmKey: 'crm.deals.close.wonConfirm',
    variant: 'default',
    requiresText: false,
  },
  lost: {
    titleKey: 'crm.deals.close.lostTitle',
    labelKey: 'crm.deals.lostReason',
    confirmKey: 'crm.deals.close.lostConfirm',
    variant: 'destructive',
    requiresText: true,
  },
};

interface Props {
  open: boolean;
  mode: CloseMode;
  deal: CrmDealListItem | null;
  onConfirm: (input: { lostReason?: string; note?: string }) => Promise<void>;
  onCancel: () => void;
}

/**
 * The one confirmation the board asks for: lost needs a reason (server 400
 * otherwise), won irreversibly flags the account as a customer. Enter
 * confirms a win (Shift+Enter breaks a line in the note), Cmd/Ctrl+Enter
 * confirms a loss (the reason textarea owns plain Enter); a focused button
 * always keeps Enter for itself.
 */
export function CloseDealDialog({ open, mode, deal, onConfirm, onCancel }: Props) {
  const [text, setText] = useState('');
  const [chip, setChip] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) {
      setText('');
      setChip('');
      setError(null);
      setSubmitting(false);
    }
  }, [open, deal?.id, mode]);

  const { titleKey, labelKey, confirmKey, variant, requiresText } = MODES[mode];
  const value = deal?.valueNet === null || deal === null ? '—' : formatMoney(deal.valueNet, deal.currency);
  const body = requiresText
    ? deal
      ? `${deal.name} · ${deal.accountName} · ${value}`
      : ''
    : t('crm.deals.close.wonBody', { account: deal?.accountName ?? '', value });
  const textTooShort = requiresText && text.trim().length < MIN_REASON_LENGTH;

  const submit = async () => {
    if (submitting) return;
    const trimmed = text.trim();
    if (textTooShort) {
      setError(t('crm.deals.lostReasonRequired'));
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm(requiresText ? { lostReason: trimmed } : trimmed ? { note: trimmed } : {});
    } catch (caught) {
      setError((caught as Error)?.message || t('errors.saveFailed'));
      setSubmitting(false);
    }
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
    // A focused button owns Enter (the browser clicks it): Enter on "Anuluj"
    // must cancel, never confirm a win behind the user's back.
    if (event.target instanceof HTMLButtonElement) return;
    if (!requiresText) {
      // Plain Enter anywhere — including the autofocused note — confirms;
      // Shift+Enter is the one way to put a line break in the note.
      if (event.shiftKey) return;
      event.preventDefault();
      void submit();
      return;
    }
    if (event.metaKey || event.ctrlKey) {
      event.preventDefault();
      void submit();
    }
  };

  const selectChip = (key: string) => {
    setChip(key);
    if (key) setText(t(`crm.deals.close.reasons.${key}`));
  };

  return (
    <Dialog open={open} onOpenChange={(next) => !next && !submitting && onCancel()}>
      <DialogContent className="sm:max-w-md" onKeyDown={handleKeyDown}>
        <DialogHeader>
          <DialogTitle>{t(titleKey)}</DialogTitle>
          <DialogDescription>{body}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {requiresText ? (
            <ToggleGroup
              type="single"
              size="sm"
              variant="outline"
              spacing={1}
              className="flex-wrap"
              value={chip}
              onValueChange={selectChip}
            >
              {LOST_REASONS.map((key) => (
                <ToggleGroupItem key={key} value={key} className="rounded-md! border! text-xs">
                  {t(`crm.deals.close.reasons.${key}`)}
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          ) : null}
          <div className="space-y-1.5">
            <Label htmlFor="close-deal-text">{t(labelKey)}</Label>
            <Textarea
              id="close-deal-text"
              autoFocus
              rows={3}
              value={text}
              aria-invalid={!!error || undefined}
              onChange={(event) => {
                setText(event.target.value);
                // Editing the prefilled text means it is no longer "that" chip.
                if (chip && event.target.value !== t(`crm.deals.close.reasons.${chip}`)) setChip('');
                if (error) setError(null);
              }}
            />
            {error ? <p className="text-destructive text-xs">{error}</p> : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel} disabled={submitting}>
            {t('common.cancel')}
          </Button>
          <Button variant={variant} onClick={() => void submit()} disabled={submitting || textTooShort}>
            {submitting ? <Loader2 className="size-4 animate-spin" /> : null}
            {t(confirmKey)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
